// src/services/employeeAuditService.js

/**
 * Employee Audit Service
 * Tracks all changes to employee data with comprehensive audit trail
 * Stores audit entries in a subcollection for each employee
 */

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";

class EmployeeAuditService {
  /**
   * Create an audit entry for employee changes
   * @param {string} companyId - Company ID
   * @param {string} employeeId - Employee document ID
   * @param {Object} changes - Map of field changes
   * @param {string} userId - User making the change
   * @param {Object} userInfo - Additional user information
   * @returns {Promise<Object>} Result of the operation
   */
  async createAuditEntry(companyId, employeeId, changes, userId, userInfo = {}) {
    try {
      if (!companyId || !employeeId || !userId) {
        throw new Error("Missing required parameters");
      }

      const auditRef = collection(
        db,
        "companies",
        companyId,
        "users",
        employeeId,
        "auditTrail"
      );

      const auditEntry = {
        timestamp: serverTimestamp(),
        action: Object.keys(changes).length === 0 ? "create" : "update",
        userId,
        userEmail: userInfo.email || "",
        userName: userInfo.displayName || userInfo.name || "",
        changes,
        metadata: {
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
          ipAddress: "", // Can be populated from server-side if needed
        },
      };

      const docRef = await addDoc(auditRef, auditEntry);

      return {
        success: true,
        auditId: docRef.id,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error("Error creating audit entry:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Create batch audit entries for multiple field changes
   * @param {string} companyId - Company ID
   * @param {string} employeeId - Employee document ID
   * @param {Map} changeMap - Map of all field changes
   * @param {string} userId - User making the changes
   * @param {Object} userInfo - Additional user information
   * @returns {Promise<Object>} Result of the operation
   */
  async createBatchAuditEntry(companyId, employeeId, changeMap, userId, userInfo = {}) {
    try {
      // Convert change map to a single audit entry with all changes
      const changes = {};

      changeMap.forEach((value, fieldPath) => {
        changes[fieldPath] = value;
      });

      return await this.createAuditEntry(companyId, employeeId, changes, userId, userInfo);
    } catch (error) {
      console.error("Error creating batch audit entry:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Compare two objects and return the differences
   * @param {Object} oldData - Original data
   * @param {Object} newData - New data
   * @param {string} path - Current path in object (for nested objects)
   * @returns {Object} Map of changes with old and new values
   */
  compareObjects(oldData, newData, path = "") {
    const changes = {};

    // Handle null/undefined cases
    if (!oldData) oldData = {};
    if (!newData) newData = {};

    // Get all unique keys from both objects
    const allKeys = new Set([
      ...Object.keys(oldData),
      ...Object.keys(newData),
    ]);

    allKeys.forEach((key) => {
      const currentPath = path ? `${path}.${key}` : key;
      const oldValue = oldData[key];
      const newValue = newData[key];

      // Skip if both values are undefined or null
      if (
        (oldValue === undefined || oldValue === null || oldValue === "") &&
        (newValue === undefined || newValue === null || newValue === "")
      ) {
        return;
      }

      // Check if values are different
      if (oldValue !== newValue) {
        // Handle nested objects (but not arrays or dates)
        if (
          typeof oldValue === "object" &&
          typeof newValue === "object" &&
          !Array.isArray(oldValue) &&
          !Array.isArray(newValue) &&
          !(oldValue instanceof Date) &&
          !(newValue instanceof Date) &&
          oldValue !== null &&
          newValue !== null
        ) {
          // Recursively compare nested objects
          const nestedChanges = this.compareObjects(oldValue, newValue, currentPath);
          Object.assign(changes, nestedChanges);
        } else {
          // Record the change
          changes[currentPath] = {
            oldValue: oldValue ?? null,
            newValue: newValue ?? null,
          };
        }
      }
    });

    return changes;
  }

  /**
   * Get the complete audit trail for an employee
   * @param {string} companyId - Company ID
   * @param {string} employeeId - Employee document ID
   * @param {Object} filters - Optional filters
   * @returns {Promise<Array>} Array of audit entries
   */
  async getEmployeeAuditTrail(companyId, employeeId, filters = {}) {
    try {
      const auditRef = collection(
        db,
        "companies",
        companyId,
        "users",
        employeeId,
        "auditTrail"
      );

      let q = query(auditRef, orderBy("timestamp", "desc"));

      // Apply filters if provided
      if (filters.userId) {
        q = query(q, where("userId", "==", filters.userId));
      }

      if (filters.action) {
        q = query(q, where("action", "==", filters.action));
      }

      if (filters.startDate) {
        q = query(
          q,
          where("timestamp", ">=", Timestamp.fromDate(new Date(filters.startDate)))
        );
      }

      if (filters.endDate) {
        q = query(
          q,
          where("timestamp", "<=", Timestamp.fromDate(new Date(filters.endDate)))
        );
      }

      if (filters.limit) {
        q = query(q, limit(filters.limit));
      }

      const snapshot = await getDocs(q);
      const auditTrail = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        auditTrail.push({
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate?.() || data.timestamp,
        });
      });

      return auditTrail;
    } catch (error) {
      console.error("Error fetching audit trail:", error);
      return [];
    }
  }

  /**
   * Get the history of changes for a specific field
   * @param {string} companyId - Company ID
   * @param {string} employeeId - Employee document ID
   * @param {string} fieldPath - Field path to track (e.g., "employment.salary")
   * @returns {Promise<Array>} Array of changes for the field
   */
  async getFieldHistory(companyId, employeeId, fieldPath) {
    try {
      const auditTrail = await this.getEmployeeAuditTrail(companyId, employeeId);

      const fieldHistory = [];

      auditTrail.forEach((entry) => {
        if (entry.changes && entry.changes[fieldPath]) {
          fieldHistory.push({
            timestamp: entry.timestamp,
            userId: entry.userId,
            userEmail: entry.userEmail,
            userName: entry.userName,
            ...entry.changes[fieldPath],
          });
        }
      });

      return fieldHistory;
    } catch (error) {
      console.error("Error fetching field history:", error);
      return [];
    }
  }

  /**
   * Create an audit entry for compensation changes
   * This is a specialized method for tracking compensation-specific changes
   * @param {string} companyId - Company ID
   * @param {string} employeeId - Employee document ID
   * @param {Object} compensationChanges - Compensation-specific changes
   * @param {string} effectiveDate - When the changes take effect
   * @param {string} userId - User making the change
   * @param {Object} userInfo - Additional user information
   * @returns {Promise<Object>} Result of the operation
   */
  async createCompensationAuditEntry(
    companyId,
    employeeId,
    compensationChanges,
    effectiveDate,
    userId,
    userInfo = {}
  ) {
    try {
      // Add effective date to each compensation change
      const changesWithEffectiveDate = {};

      Object.keys(compensationChanges).forEach((field) => {
        changesWithEffectiveDate[field] = {
          ...compensationChanges[field],
          effectiveDate,
        };
      });

      return await this.createAuditEntry(
        companyId,
        employeeId,
        changesWithEffectiveDate,
        userId,
        userInfo
      );
    } catch (error) {
      console.error("Error creating compensation audit entry:", error);
      return {
        success: false,
        error: error.message,
      };
    }
  }

  /**
   * Get audit statistics for an employee
   * @param {string} companyId - Company ID
   * @param {string} employeeId - Employee document ID
   * @returns {Promise<Object>} Statistics object
   */
  async getAuditStatistics(companyId, employeeId) {
    try {
      const auditTrail = await this.getEmployeeAuditTrail(companyId, employeeId);

      const stats = {
        totalChanges: auditTrail.length,
        changesByUser: {},
        changesByField: {},
        lastModified: null,
        createdAt: null,
      };

      auditTrail.forEach((entry, index) => {
        // Track changes by user
        const userName = entry.userName || entry.userEmail || entry.userId;
        if (!stats.changesByUser[userName]) {
          stats.changesByUser[userName] = 0;
        }
        stats.changesByUser[userName]++;

        // Track changes by field
        if (entry.changes) {
          Object.keys(entry.changes).forEach((field) => {
            if (!stats.changesByField[field]) {
              stats.changesByField[field] = 0;
            }
            stats.changesByField[field]++;
          });
        }

        // Track timestamps
        if (index === 0) {
          stats.lastModified = entry.timestamp;
        }
        if (index === auditTrail.length - 1) {
          stats.createdAt = entry.timestamp;
        }
      });

      return stats;
    } catch (error) {
      console.error("Error fetching audit statistics:", error);
      return null;
    }
  }
}

// Export singleton instance
const employeeAuditService = new EmployeeAuditService();
export default employeeAuditService;